import * as React from 'react';
import { useNotify, useRecordContext, useTranslate } from 'react-admin';
import { useCanAccess } from '@react-admin/ra-rbac';
import Button from '@mui/material/Button';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { Alert } from '@mui/material';
import { StatsTooltip } from '../../components/statistics/common';

const APP_IDS = {
    teacher: '0e274549-7a89-4313-8720-0f4588a51f98',
    student: 'a7faf0f2-3cfc-4f62-9a77-aa75134ac5a4',
    pupil: '5b846602-4428-4c2d-b12a-d2b56dd722b7',
};

// Järjestelmänvalvojalle (userrights >= 400): kopioi käyttäjän access_token leikepöydälle observer-tiedolla.
// source-propia ei käytetä itse komponentissa, mutta DatagridConfigurable tunnistaa sarakkeen sen perusteella (ks. omit-propi listanäkymissä).
const CustomCopyAccessTokenButton = ({
    role,
}: {
    role: 'teacher' | 'student' | 'pupil';
    source?: string;
    label?: string;
}) => {
    const record = useRecordContext();
    const translate = useTranslate();
    const notify = useNotify();
    const { canAccess } = useCanAccess({
        action: 'copy_access_token',
        resource: role,
    });

    const isActive = record?.status === 0;

    const handleClick = async e => {
        e.stopPropagation();

        const api = `${process.env.REACT_APP_INFRA_API}/aspa/create_access_token`;
        const token = localStorage.getItem('token');

        try {
            const response = await fetch(api, {
                method: 'POST',
                headers: {
                    Accept: 'application/json',
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    user_guid: record.id,
                    app_id: APP_IDS[role],
                }),
            });

            if (response.status >= 400) {
                throw new Error('Bad response from server');
            }

            const data = await response.json();
            await navigator.clipboard.writeText(data.access_token);
            notify(
                <Alert severity="info">
                    {translate('actions.copy_access_token.copied')}
                </Alert>
            );
        } catch (e) {
            console.error(e);
            notify(`notifications.MATERIAL_PREVIEW_ERROR`, { type: 'warning' });
        }
    };

    if (!canAccess) return null;

    return (
        <StatsTooltip
            title={
                isActive
                    ? translate('actions.copy_access_token.tooltip')
                    : translate('actions.copy_access_token.user_inactive')
            }
            followCursor
            placement="top-start"
            componentsProps={{
                tooltip: {
                    sx: {
                        padding: '12px 16px',
                        fontSize: '14px',
                        lineHeight: 1.4,
                    },
                },
            }}
        >
            <span>
                <Button onClick={handleClick} disabled={!isActive} size="small">
                    <ContentCopyIcon />
                </Button>
            </span>
        </StatsTooltip>
    );
};

export default CustomCopyAccessTokenButton;
